import { OfferingCategory } from "@albertine/types";
import { z } from "zod";
import { omitUndefined } from "../../utils/object.util";
import { CatalogItemId } from "../CatalogItem";
import { CurrencyCode, Price } from "../currency";
import {
    AgentId,
    FirebaseId,
    GeoPoint,
    MediumLongString,
    MemberId,
    PercentCrop,
    PixelCrop,
    Rating,
    UrlString,
} from "../validate";
import { BookingAttachment, ImageAttachment } from "./Attachment";
import { BookingOption } from "./BookingOption";
import { BookingReservationStatus } from "./BookingReservationStatus";
import { FirebaseTimestamp } from "./FirebaseTimestamp";

const BookingRequired = z.object({
    catalogItem: CatalogItemId.optional().nullable(),
    createdBy: AgentId,
    member: MemberId,
    request: FirebaseId,

    title: z.string().nullable(),
    archived: z.boolean(),
});

export const BookingMeta = z.object({
    createdAt: FirebaseTimestamp,
    updatedAt: FirebaseTimestamp.nullable().optional(),
    updatedBy: AgentId.optional().nullable(),

    prebookedAt: FirebaseTimestamp.optional().nullable(),
    confirmedAt: FirebaseTimestamp.optional().nullable(),
    cancelledAt: FirebaseTimestamp.optional().nullable(),
    calendarEventId: z.string().nullable(),
});
export type BookingMeta = z.infer<typeof BookingMeta>;

export const BookingUpdatables = z.object({
    title: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
    website: UrlString.optional().nullable(),
    phoneNumber: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    offeringCategory: OfferingCategory.optional().nullable(),
    googleUrl: z.string().optional().nullable(),
    geoPoint: GeoPoint.optional().nullable(),
    numberOfGuests: z.number().optional().nullable(),
    numberOfNights: z.number().optional().nullable(),
    rooms: z.string().optional().nullable(),
    price: Price.optional().nullable(),
    priceCurrency: CurrencyCode.optional().nullable(),
    priceQuote: z.enum(["final", "estimate"]).optional().nullable(),
    paymentTo: z.enum(["albertine", "supplier"]).optional().nullable(),
    priceDetails: z.string().optional().nullable(),

    attachments: z.array(BookingAttachment).optional(),

    imageURL: z.string().optional().nullable(),
    absoluteCrop: PixelCrop.optional().nullable(),
    relativeCrop: PercentCrop.optional().nullable(),
    croppedImageURL: z.string().optional().nullable(),
    images: z.array(ImageAttachment).optional().nullable(),

    reference: z.string().optional().nullable(),
    reservationStatus: BookingReservationStatus.optional().nullable(),
    notesForMember: z.string().optional().nullable(),
    notesForMemberSenderId: AgentId.optional().nullable(),
    notesForMemberSenderName: z.string().optional().nullable(),

    suppliers: z.array(z.string()).optional().nullable(),
    supplierDetails: z.string().optional().nullable(),

    startTime: FirebaseTimestamp.optional().nullable(),
    startTimezone: z.string().optional().nullable(), // TODO: Create common type
    endTime: FirebaseTimestamp.optional().nullable(),
    endTimezone: z.string().optional().nullable(), // TODO: Create common type
    archived: z.boolean().optional(),
    reviewNeeded: z.boolean().optional().nullable(),

    headline: z.string().optional().nullable(),
    benefits: z.string().optional().nullable(),
    bookingPolicy: z.string().optional().nullable(),

    options: z.array(BookingOption).optional().nullable(),
});
export type BookingUpdatables = z.infer<typeof BookingUpdatables>;

export const BookingFeedback = z.object({
    bookingRating: Rating,
    bookingFeedback: MediumLongString.optional().nullable(),
    bookingFeedbackTimestamp: FirebaseTimestamp.optional().nullable(),
});
export type BookingFeedback = z.infer<typeof BookingFeedback>;

export const ProposalFeedbackEnum = z.enum(["like", "dislike"]);

export type ProposalFeedbackEnum = z.infer<typeof ProposalFeedbackEnum>;

export const ProposalFeedback = z.object({
    proposalFeedback: ProposalFeedbackEnum.optional().nullable(),
    proposalFeedbackTimestamp: FirebaseTimestamp.optional().nullable(),
});
export type ProposalFeedback = z.infer<typeof ProposalFeedback>;

const BookingId = z.object({ id: FirebaseId });

export const BookingDoc = BookingRequired.extend(BookingMeta.shape)
    .extend(BookingUpdatables.shape)
    .extend(ProposalFeedback.shape)
    .extend(BookingFeedback.shape);
export type BookingDoc = z.infer<typeof BookingDoc>;

export const BookingPayload = BookingRequired.extend(BookingUpdatables.shape);
export type BookingPayload = z.infer<typeof BookingPayload>;

export const Booking = BookingId.extend(BookingDoc.shape);
export type Booking = z.infer<typeof Booking>;

export const BookingInMessage = BookingId.extend(
    BookingRequired.pick({
        request: true,
        title: true,
    }).shape,
).extend(
    BookingUpdatables.omit({
        supplierDetails: true,
    }).shape,
);
export type BookingInMessage = z.infer<typeof BookingInMessage>;

export const BookingToConfirmInMemberMessage = BookingId.extend(
    BookingRequired.pick({
        title: true,
    }).shape,
).extend(
    BookingUpdatables.omit({
        supplierDetails: true,
    }).shape,
);
export type BookingToConfirmInMemberMessage = z.infer<
    typeof BookingToConfirmInMemberMessage
>;

export const parseBookingToMessage = (booking: Booking): BookingInMessage => {
    const bookingToMessage = BookingInMessage.parse(booking);
    if (bookingToMessage.croppedImageURL) {
        bookingToMessage.imageURL = booking.croppedImageURL;
    }
    // Firebase callable encode maps undefined → null; omit empty attachments.
    return omitUndefined({
        ...bookingToMessage,
        attachments: bookingToMessage.attachments ?? undefined,
    });
};

export const CreateBookingRequest = BookingRequired.omit({
    createdBy: true,
    member: true,
}).extend(BookingUpdatables.omit({ offeringCategory: true }).shape);
export type CreateBookingRequest = z.infer<typeof CreateBookingRequest>;

const CreateBookingResponse = z.discriminatedUnion("success", [
    z.object({ success: z.literal(true), bookingId: FirebaseId }),
    z.object({ success: z.literal(false), error: z.string() }),
]);

export type CreateBookingResponse = z.infer<typeof CreateBookingResponse>;

export const CreateCopiedBooking = BookingRequired.extend(
    BookingUpdatables.omit({
        attachments: true,
        numberOfNights: true,
        rooms: true,
        reference: true,
        reservationStatus: true,
    }).shape,
)
    .extend(
        BookingMeta.pick({
            calendarEventId: true,
            createdAt: true,
            updatedAt: true,
        }).shape,
    )
    .extend(
        z.object({
            reviewNeeded: z.literal(true),
            calendarEventId: z.literal(null),
        }).shape,
    )
    .required();
export type CreateCopiedBooking = z.infer<typeof CreateCopiedBooking>;

export const MemberBookingDoc = BookingDoc.pick({
    title: true,
    description: true,
    offeringCategory: true,
    imageURL: true,
    croppedImageURL: true,
    images: true,
    attachments: true,
    startTime: true,
    startTimezone: true,
    endTime: true,
    endTimezone: true,
    numberOfGuests: true,
    numberOfNights: true,
    website: true,
    address: true,
    phoneNumber: true,
    geoPoint: true,
    googleUrl: true,
    notesForMember: true,
    notesForMemberSenderId: true,
    notesForMemberSenderName: true,
    rooms: true,
    benefits: true,
    price: true,
    priceDetails: true,
    reference: true,
    request: true,
    bookingFeedbackTimestamp: true,
    bookingRating: true,
    bookingFeedback: true,
    headline: true,
    bookingPolicy: true,
    proposalFeedback: true,
    reservationStatus: true,
    options: true,
});
export type MemberBookingDoc = z.infer<typeof MemberBookingDoc>;

export const MemberBooking = z
    .object({ id: FirebaseId })
    .extend(MemberBookingDoc.shape);
export type MemberBooking = z.infer<typeof MemberBooking>;

export function bookingCanHaveCalendarEvent(booking: Booking): boolean {
    return (
        booking.reservationStatus === "confirmed" &&
        !!booking.startTime &&
        !!booking.startTimezone &&
        !!booking.endTime &&
        !!booking.endTimezone
    );
}

export function isValidNumberOfGuests(value: string): boolean {
    return z.number().int().gte(1).safeParse(Number(value)).success;
}

export function isValidPrice(value: string): boolean {
    return z.number().gte(0).safeParse(Number(value)).success;
}
